import styled from 'styled-components';
import { ButtonIcon, ButtonIconBg, BackButtonIconBg } from '@comeon/ui';
import Icon, { ICON_CLOSE, ICON_ARROW_LEFT } from '~/components/icon/index.js';
import { getZindexByType } from '~/utils/style-utils.ts';
import SimpleSidebarHeader from './simple-sidebar-header.js';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { useAppSelector } from '~/store/types';

const StyledDefaultHeader = styled(SimpleSidebarHeader)`
	height: var(--sidebarHeaderHeight);
	white-space: nowrap;
	z-index: ${props => (props.zIndex ? `${props.zIndex}` : `${getZindexByType('header')}`)};
`;

export const LeftSideIconType = Object.freeze({
	NONE: 'NONE',
	CLOSE_ICON: 'CLOSE_ICON',
	CLOSE_ICON_BG: 'CLOSE_ICON_BG',
	BACK_ICON: 'BACK_ICON',
	BACK_ICON_BG: 'BACK_ICON_BG'
});

/**
 * LeftSideIcon component that displays an icon on the left side of a UI element.
 *
 * @param {Object} props - The props for the LeftSideIcon component.
 * @param {('edit'|'delete'|'none'|...)} [props.type=LeftSideIconType.NONE] - The type of the icon to display. Should be one of the values from `LeftSideIconType` (optional, default is `NONE`).
 * @param {Function} props.onClick - The function to call when the icon is clicked (required).
 *
 * @returns {JSX.Element} The LeftSideIcon component JSX.
 */
const LeftSideIcon = ({ onClick, type = LeftSideIconType.NONE }) => {
	if (!type || type === LeftSideIconType.NONE) {
		return null;
	}

	if (type === LeftSideIconType.CLOSE_ICON) {
		return (
			<ButtonIcon onClick={onClick} dataAt="close-button-general" data-testid="sidebar-header__close-icon-button">
				<Icon type={ICON_CLOSE} />
			</ButtonIcon>
		);
	}

	if (type === LeftSideIconType.CLOSE_ICON_BG) {
		return (
			<ButtonIconBg
				onClick={onClick}
				dataAt="close-button-general"
				data-testid="sidebar-header__close-icon-button"
			>
				<Icon type={ICON_CLOSE} />
			</ButtonIconBg>
		);
	}

	if (type === LeftSideIconType.BACK_ICON) {
		return (
			<ButtonIcon onClick={onClick} data-testid="sidebar-header__back-icon-button" dataAt="back-button-general">
				<Icon type={ICON_ARROW_LEFT} data-testid="sidebar-header__back-icon-button" />
			</ButtonIcon>
		);
	}

	if (type === LeftSideIconType.BACK_ICON_BG) {
		return (
			<BackButtonIconBg
				onClick={onClick}
				data-testid="sidebar-header__back-icon-button"
				dataAt="back-button-general"
			>
				<Icon type={ICON_ARROW_LEFT} data-testid="sidebar-header__back-icon-button" />
			</BackButtonIconBg>
		);
	}
};

/**
 * SidebarHeader component that renders the header section of a sidebar with optional icons, buttons, and styles.
 *
 * @param {Object} props - The props for the SidebarHeader component.
 * @param {React.ReactNode} props.children - The content to display inside the header.
 * @param {string} [props.className=''] - Additional class name(s) for custom styling (optional).
 * @param {string} [props.leftSideIcon=LeftSideIconType.NONE] - The type of left side icon to display. One of the values from `LeftSideIconType` (optional, defaults to `NONE`).
 * @param {Function} [props.onLeftSideButtonClick] - The function to call when the left side button/icon is clicked (optional).
 * @param {React.ReactNode} [props.Icon=null] - A custom icon node to display in the left side of the header (optional).
 * @param {boolean} [props.sticky=true] - Whether the header should be sticky on desktop (optional, defaults to `true`).
 * @param {boolean} [props.stickySmaller=true] - Whether the header should be sticky on mobile (optional, defaults to `true`).
 * @param {boolean} [props.transparent=false] - Whether the header should have a transparent background (optional, defaults to `false`).
 * @param {boolean} [props.transparentSmaller=false] - Whether the header should have a transparent background on mobile screens (optional, defaults to `false`).
 * @param {boolean} [props.transparentBigger=false] - Whether the header should have a transparent background on desktop screens (optional, defaults to `false`).
 * @param {string} [props.transparentClassName='u-bg-trans'] - Class name applied when the header is transparent (optional).
 * @param {string} [props.opaqueClassName=''] - Class name applied when the header is opaque (optional).
 * @param {boolean} [props.isNew=false] - Flag indicating whether this is a new sidebar header (optional, defaults to `false`).
 *
 * @returns {JSX.Element} The SidebarHeader component JSX.
 */
const SidebarHeader = ({
	children,
	className = '',
	leftSideIcon = LeftSideIconType.NONE,
	onLeftSideButtonClick = null,
	Icon: SomeIcon = null,
	sticky = true,
	stickySmaller = true,
	transparent = false,
	transparentSmaller = false,
	transparentBigger = false,
	transparentClassName = 'u-bg-trans',
	opaqueClassName = '',
	isNew = false
}) => {
	const closeCurrentContext = useCloseCurrentContext();
	const handleCloseSidebar = event => {
		if (event) event.stopPropagation();
		if (onLeftSideButtonClick) {
			return onLeftSideButtonClick();
		}
		closeCurrentContext();
	};
	const smaller = useAppSelector(isSmallerSelector);

	const classNames = [
		className || '',
		'page-head',
		transparent || transparentBigger ? transparentClassName : opaqueClassName
	].join(' ');
	const classNamesSmaller = [
		className || '',
		'page-head',
		transparent || transparentSmaller ? transparentClassName : opaqueClassName
	].join(' ');

	// TODO: SidebarHeader needs o be fully refactored to not rely on classnames and instead use styled-components
	return (
		<StyledDefaultHeader
			className={smaller ? classNamesSmaller : classNames}
			sticky={sticky}
			stickySmaller={stickySmaller}
			isNew={isNew}
		>
			<div className="page-head__left-side">
				<LeftSideIcon type={leftSideIcon} onClick={handleCloseSidebar} />
				{SomeIcon && SomeIcon}
			</div>
			{children}
		</StyledDefaultHeader>
	);
};

export default SidebarHeader;
